'use client';
import React, { useEffect, useState } from 'react';
import { RoundedSectionCard } from '@/components/common/RoundedSectionCard';
import TimeLine from '@/components/common/TimeLine';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarPen, faCalendarRange } from '@fortawesome/pro-duotone-svg-icons';
import { Timeline } from '@/actions/dtos/tender/timeline';
import { downloadFile } from '@/utils/downloadUtil';
import { exportTimelineEntries } from '@/api/requests/tenders';
import { FeatureType, openModal } from '@/slices/features/modalSlice';
import Button from '@/components/common/Button';
import { SubscriptionTier } from '@/actions/dtos/subscription';
import { useAccount } from '@/hooks/useAccount';
import { useDispatch } from 'react-redux';

interface TimeLineEvent {
  order: number;
  title: string;
  date: Date;
  isToday?: boolean;
}

interface TimeLineSectionProps {
  timeline: Timeline;
  tenderId: string;
  isAuthenticated: boolean;
}

const TimeLineSection: React.FC<TimeLineSectionProps> = ({ timeline, tenderId, isAuthenticated }) => {
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);
  const [hasPermissionToExport, setHasPermissionToExport] = useState(false);
  const { currentTier } = useAccount();
  const dispatch = useDispatch();

  useEffect(() => {
    setHasPermissionToExport(currentTier !== SubscriptionTier.BASIC && isAuthenticated);
  }, [currentTier, isAuthenticated]);

  const timelineEvents: TimeLineEvent[] = [
    {
      title: 'Bekanntmachung',
      date: new Date(timeline.publicationBegin),
      order: 1,
    },
    {
      title: 'Veröffentlichungsende',
      date: new Date(timeline.publicationEnd),
      order: 6,
    },
    timeline.submissionBegin && {
      title: 'Abgabestart',
      date: new Date(timeline.submissionBegin),
      order: 2,
    },
    timeline.documentRequestEnd && {
      title: 'Dokumentenanfrage-Frist',
      date: new Date(timeline.documentRequestEnd),
      order: 3,
    },
    timeline.questioningEnd && {
      title: 'Bieterfragenfrist',
      date: new Date(timeline.questioningEnd),
      order: 4,
    },
    {
      title: 'Abgabefrist',
      date: new Date(timeline.submissionEnd),
      order: 5,
    },
    timeline.bindingEnd && {
      title: 'Bindefrist',
      date: new Date(timeline.bindingEnd),
      order: 7,
    },
    timeline.executionBegin && {
      title: 'Laufzeitbeginn',
      date: new Date(timeline.executionBegin),
      order: 8,
    },
    timeline.executionEnd && {
      title: 'Laufzeitende',
      date: new Date(timeline.executionEnd),
      order: 9,
    },
    {
      title: 'Heute',
      date: new Date(),
      isToday: true,
      order: 0,
    },
  ].filter((event): event is TimeLineEvent => event !== null);

  timelineEvents.sort((a, b) => {
    const dateComparison = a.date.getTime() - b.date.getTime();
    if (dateComparison !== 0) {
      return dateComparison;
    }
    return a.order - b.order;
  });

  const exportTimeline = async () => {
    try {
      setIsExportLoading(true);
      const { blob, fileName } = await exportTimelineEntries(tenderId);
      downloadFile(blob, fileName);
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleExportButtonClick = async () => {
    if (hasPermissionToExport) {
      await exportTimeline();
    } else {
      dispatch(openModal(FeatureType.ICAL_EXPORT));
    }
  };

  return (
    <RoundedSectionCard>
      <div className="overflow-auto">
        <div className="mb-3 justify-between max-sm:flex-col md:flex">
          <div className="mb-3 flex gap-3">
            <span className="flex h-7 w-6 items-center justify-center">
              <FontAwesomeIcon className="fa-fw h-6 w-6" icon={faCalendarRange} />
            </span>
            <h2 className="text-xl font-light">ZEITPLAN</h2>
          </div>
          <Button
            onClick={handleExportButtonClick}
            label={'Zum Kalender hinzufügen'}
            icon={faCalendarPen}
            locked={!hasPermissionToExport}
            isLoading={isExportLoading}
          />
        </div>

        <TimeLine timeline={timelineEvents} />
      </div>
    </RoundedSectionCard>
  );
};

export default TimeLineSection;
